import React, { useMemo } from 'react';
import {
  KatanaTranslationPreviewRow,
} from '@/types';
import { ReusableModal } from '../../modals/ReusableModal';

type TranslationPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sourcePathIds: number[];
  rows: Array<KatanaTranslationPreviewRow & { route_list?: string | null }>;
  isLoading: boolean;
  errorMessage: string | null;
};

const normalizePreviewResponse = (
  response:
    | {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: KatanaTranslationPreviewRow[];
      }
    | KatanaTranslationPreviewRow[]
    | undefined,
) => {
  if (Array.isArray(response)) {
    return {
      count: response.length,
      results: response,
    };
  }

  return {
    count: response?.count ?? 0,
    results: response?.results ?? [],
  };
};

export const TranslationPreviewModal: React.FC<TranslationPreviewModalProps> = ({
  isOpen,
  onClose,
  sourcePathIds,
  rows,
  isLoading,
  errorMessage,
}) => {
  const normalized = useMemo(() => normalizePreviewResponse(rows as any), [rows]);
  const routeFromRow = (
    row: KatanaTranslationPreviewRow & { route_list?: string | null },
  ): string => {
    const routeList = row.route_list;
    if (typeof routeList === 'string' && routeList.trim().length > 0) {
      return routeList;
    }

    if (typeof row.translation_group_key === 'string' && row.translation_group_key.trim().length > 0) {
      return row.translation_group_key;
    }

    if (typeof row.path_fingerprint === 'string' && row.path_fingerprint.trim().length > 0) {
      return row.path_fingerprint;
    }

    return '';
  };

  return (
    <ReusableModal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      header={<span>Translation Preview</span>}
      footer={
        <div className="w-100 d-flex justify-content-end">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {sourcePathIds.length === 0 && (
        <div className="alert alert-warning mb-0">
          No table results are currently loaded to preview.
        </div>
      )}

      {sourcePathIds.length > 0 && isLoading && (
        <div className="text-muted">Loading translation preview...</div>
      )}

      {errorMessage && <div className="alert alert-danger mb-3">{errorMessage}</div>}

      {sourcePathIds.length > 0 && !isLoading && !errorMessage && (
        <div>
          <div className="small text-muted mb-2">
            Preview results for {sourcePathIds.length} displayed path
            {sourcePathIds.length === 1 ? '' : 's'}: {normalized.results.length} returned
          </div>

          {normalized.results.length === 0 ? (
            <div className="text-muted">No translation rows returned for displayed paths.</div>
          ) : (
            <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
              <table className="table table-sm table-striped table-bordered mb-0">
                <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th>Route</th>
                    <th>Reason Code</th>
                    <th>Delivered Dth</th>
                    <th>Missing Physical Point IDs</th>
                    <th>Missing Node Point IDs</th>
                  </tr>
                </thead>
                <tbody>
                  {normalized.results.map((row, index) => (
                    <tr key={`${row.source_path_id ?? row.id ?? index}`}>
                      <td
                        style={{
                          maxWidth: 360,
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        }}
                      >
                        {routeFromRow(row)}
                      </td>
                      <td>{row.translation_reason_code ?? ''}</td>
                      <td>{row.delivered_dth ?? ''}</td>
                      <td>{row.missing_physical_point_ids.join(', ')}</td>
                      <td>{row.missing_node_point_ids.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReusableModal>
  );
};
